// TODO: let's revisit that
/* eslint-disable @typescript-eslint/ban-types */
import { getShellApiInstance } from '../../common/shell-api-helpers'
import { isMainWindow } from '../container/windows-management'

import type {
  Listener,
  EventConsumer,
  EventEmitter,
  EventConsumers,
  EventEmitters,
  EventConsumerFunctions,
} from './models'
import { NodeEventEmitter } from './models'

type Subscription = (value: unknown) => void

const EVENT_BUS_MAX_LISTENERS = 40

const eventNotRegistered: <T = void>() => T = () => {
  throw 'Event is not registered in the Event Bus'
}

const createListener =
  <EventPayload extends {}>(emitterPromise: Promise<NodeEventEmitter>, fnName: EventConsumerFunctions<EventPayload>) =>
  (eventName: string, listener: Listener<EventPayload>) => {
    emitterPromise.then<void>(emitter => {
      emitter[fnName](eventName, listener)
    })
    return
  }
const createListenerWithEventName =
  <EventPayload extends {}>(
    emitterPromise: Promise<NodeEventEmitter>,
    fnName: EventConsumerFunctions<EventPayload>,
    eventName: string,
  ) =>
  (listener: Listener<EventPayload>) =>
    createListener<EventPayload>(emitterPromise, fnName)(eventName, listener)

const createRegisteredEventConsumer = <EventPayload extends {}>(
  emitterPromise: Promise<NodeEventEmitter>,
  eventName: string,
): EventConsumer<EventPayload> => ({
  on: createListenerWithEventName<EventPayload>(emitterPromise, 'on', eventName),
  once: createListenerWithEventName<EventPayload>(emitterPromise, 'once', eventName),
  addListener: createListenerWithEventName<EventPayload>(emitterPromise, 'addListener', eventName),
  removeListener: createListenerWithEventName<EventPayload>(emitterPromise, 'removeListener', eventName),
  prependListener: createListenerWithEventName<EventPayload>(emitterPromise, 'prependListener', eventName),
  prependOnceListener: createListenerWithEventName<EventPayload>(emitterPromise, 'prependOnceListener', eventName),
})

const createRegisteredEventEmitter = <EventPayload extends object>(
  emitterPromise: Promise<NodeEventEmitter>,
  eventName: string,
): EventEmitter<EventPayload> => {
  const emitFn = (payload: EventPayload) => emitterPromise.then(emitter => emitter.emit(eventName, payload))
  return Object.assign(emitFn, {
    eventName,
    emit: emitFn,
    ...createRegisteredEventConsumer<EventPayload>(emitterPromise, eventName),
  })
}

const convertToNodeEventEmitter = <EventEmitter extends EventConsumers & NodeEventEmitter>(
  emitterPromise: Promise<EventEmitter>,
): EventEmitter => {
  const eventEmitter = {
    on: createListener(emitterPromise, 'on'),
    once: createListener(emitterPromise, 'once'),
    addListener: createListener(emitterPromise, 'addListener'),
    removeListener: createListener(emitterPromise, 'removeListener'),
    prependListener: createListener(emitterPromise, 'prependListener'),
    prependOnceListener: createListener(emitterPromise, 'prependOnceListener'),
  }

  const proxyHandler: ProxyHandler<EventEmitter> = {
    get: (target, eventName: string) => {
      if (eventName in target) {
        return target[eventName]
      }
      return createRegisteredEventConsumer(emitterPromise, eventName)
    },
  }
  return new Proxy(eventEmitter, proxyHandler) as EventEmitter
}

export const eventEmitter = <EventPayload>(): EventEmitter<EventPayload> =>
  Object.assign(async () => eventNotRegistered<boolean>(), {
    emit: async () => eventNotRegistered<boolean>(),
    on: eventNotRegistered,
    once: eventNotRegistered,
    addListener: eventNotRegistered,
    removeListener: eventNotRegistered,
    prependListener: eventNotRegistered,
    prependOnceListener: eventNotRegistered,
    eventName: 'Not Registered',
  })

export class EventBusImpl {
  private readonly eventBusRegistry = new Map<string, EventEmitters & NodeEventEmitter>()
  private readonly promiseResolveRegistry = new Map<string, Subscription[]>()

  register<EventEmittersDict extends EventEmitters>(namespace: string, events: EventEmittersDict) {
    if (this.eventBusRegistry.has(namespace)) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        emit: this.eventBusRegistry.get(namespace)!,
        subscribeTo: <Namespace extends string = string, EventConsumerDict extends EventConsumers = {}>(
          namespace: Namespace,
        ) => this.subscribeTo<Namespace, EventConsumerDict>(namespace),
      }
    }
    // Add syntactic sugar for emitting events
    const emitter = Object.keys(events).reduce((emitter, eventName) => {
      Object.defineProperty(emitter, eventName, {
        value: createRegisteredEventEmitter(Promise.resolve(emitter), eventName),
      })
      emitter.setMaxListeners(EVENT_BUS_MAX_LISTENERS)
      return emitter
    }, new NodeEventEmitter() as NodeEventEmitter & EventEmittersDict)

    this.eventBusRegistry.set(namespace, emitter)
    const promises = this.promiseResolveRegistry.get(namespace)
    if (promises) {
      promises.forEach(promise => promise(convertToNodeEventEmitter(Promise.resolve(emitter))))
      this.promiseResolveRegistry.delete(namespace)
    }
    return {
      emit: emitter as EventEmittersDict,
      subscribeTo: <Namespace extends string = string, EventConsumerDict extends EventConsumers = {}>(
        namespace: Namespace,
      ) => this.subscribeTo<Namespace, EventConsumerDict>(namespace),
    }
  }

  subscribeTo<Namespace extends string = string, EventConsumerDict extends EventConsumers = {}>(namespace: Namespace) {
    const emitterName = Array.from(this.eventBusRegistry.keys()).find(
      registeredNamespace => registeredNamespace === namespace,
    )
    if (emitterName) {
      return convertToNodeEventEmitter<NodeEventEmitter & EventConsumerDict>(
        Promise.resolve(this.eventBusRegistry.get(emitterName) as unknown as NodeEventEmitter & EventConsumerDict),
      )
    }
    const promise = new Promise<NodeEventEmitter & EventConsumerDict>(resolve => {
      const promises = this.promiseResolveRegistry.get(namespace)
      if (promises) {
        promises.push(resolve as Subscription)
      } else {
        this.promiseResolveRegistry.set(namespace, [resolve as Subscription])
      }
    })

    return convertToNodeEventEmitter<NodeEventEmitter & EventConsumerDict>(promise)
  }
}

export type EventBus = EventBusImpl

/**
 * The EventBus can be used to register Events from an Experience / Element
 * as well as to be able to react to events emitted by other Experiences or Elements.
 *
 * The EventBus uses the Node 'events' library but hides the interface and instead
 * uses syntactic sugar to easily refer to events that were declared by other Experiences / Elements.
 */
let eventBus!: EventBus

if (isMainWindow()) {
  eventBus = new EventBusImpl()
}

export const getEventBus = (): EventBus => (isMainWindow() ? eventBus : getShellApiInstance().eventBus)
